import { useState, useEffect, useCallback, useRef } from "react";
import rfqNotificationsService from "services/rfqNotifications.service";
import { nextSortDirection, CUSTOMER_SORT_FIELD } from "../utils/sortUtils";

const useCustomerSelection = (initialSelectedCustomers = [], options = {}) => {
  const { searchDebounceMs = 300, limit = 10, sourceIds = [] } = options;

  // Available customers state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(
    new Set(initialSelectedCustomers || []),
  );
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allCustomers, setAllCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);

  // Sort state for available customers panel
  const [sortDirection, setSortDirection] = useState(null);

  // Selected customers state
  const [selectedSearchQuery, setSelectedSearchQuery] = useState("");
  const [debouncedSelectedSearchQuery, setDebouncedSelectedSearchQuery] =
    useState("");
  const [selectedCustomersData, setSelectedCustomersData] = useState([]);
  const [selectedCustomersOffset, setSelectedCustomersOffset] = useState(0);
  const [selectedCustomersHasMore, setSelectedCustomersHasMore] =
    useState(false);
  const [selectedCustomersLoading, setSelectedCustomersLoading] =
    useState(false);

  // Sort state for selected customers panel
  const [selectedSortDirection, setSelectedSortDirection] = useState(null);

  const lastFetchedCustomerIds = useRef(null);
  const lastSearchQueryRef = useRef("");

  // AbortControllers for request cancellation
  const availableAbortRef = useRef(null);
  const selectedAbortRef = useRef(null);

  // Derive sort params
  const availableSortField = sortDirection ? CUSTOMER_SORT_FIELD : "";
  const availableSortDir = sortDirection || "";
  const selectedSortField = selectedSortDirection ? CUSTOMER_SORT_FIELD : "";
  const selectedSortDir = selectedSortDirection || "";

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, searchDebounceMs);
    return () => clearTimeout(timer);
  }, [searchQuery, searchDebounceMs]);

  // Debounce selected customers search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSelectedSearchQuery(selectedSearchQuery);
    }, searchDebounceMs);
    return () => clearTimeout(timer);
  }, [selectedSearchQuery, searchDebounceMs]);

  // Fetch available customers when search or sort changes
  useEffect(() => {
    const fetchCustomers = async () => {
      // Abort previous request
      if (availableAbortRef.current) {
        availableAbortRef.current.abort();
      }
      const controller = new AbortController();
      availableAbortRef.current = controller;

      setCustomersLoading(true);
      setCustomersError(null);
      setCurrentOffset(0);
      setHasMore(true);

      try {
        const data = await rfqNotificationsService.getCustomers(
          debouncedSearchQuery,
          limit,
          0,
          availableSortField,
          availableSortDir,
          controller.signal,
          sourceIds,
        );
        setAllCustomers(data.results || []);
        setHasMore((data.results || []).length === limit);
        setCustomersLoading(false);
      } catch (error) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          // Aborted by a newer request — do NOT clear loading, the new
          // request has already set it to true.
          return;
        }
        console.error("Failed to fetch customers:", error);
        setCustomersError("Failed to load customers");
        setAllCustomers([]);
        setCustomersLoading(false);
      }
    };

    fetchCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, limit, availableSortField, availableSortDir, JSON.stringify(sourceIds)]);

  // Fetch selected customers data
  const fetchSelectedCustomersData = useCallback(
    async (customerIds, offset = 0, append = false, search = "") => {
      if (!customerIds || customerIds.length === 0) {
        setSelectedCustomersData([]);
        setSelectedCustomersHasMore(false);
        return;
      }

      // Abort previous selected customers request
      if (selectedAbortRef.current) {
        selectedAbortRef.current.abort();
      }
      const controller = new AbortController();
      selectedAbortRef.current = controller;

      setSelectedCustomersLoading(true);

      try {
        const data = await rfqNotificationsService.getSelectedCustomers(
          customerIds,
          limit,
          offset,
          search,
          selectedSortField,
          selectedSortDir,
          controller.signal,
        );
        const newResults = data.results || [];

        if (append) {
          setSelectedCustomersData((prev) => [...prev, ...newResults]);
        } else {
          setSelectedCustomersData(newResults);
        }

        setSelectedCustomersHasMore(
          newResults.length === limit && offset + limit < customerIds.length,
        );
        setSelectedCustomersLoading(false);
      } catch (error) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          // Aborted by a newer request — do NOT clear loading, the new
          // request has already set it to true.
          return;
        }
        console.error("Failed to fetch selected customers data:", error);
        if (!append) {
          setSelectedCustomersData([]);
          setSelectedCustomersHasMore(false);
        }
        setSelectedCustomersLoading(false);
      }
    },
    [limit, selectedSortField, selectedSortDir],
  );

  // Fetch selected customers data when selection or search changes
  useEffect(() => {
    const customerIds = Array.from(selectedCustomerIds).filter(
      (id) => id !== null && id !== undefined,
    );
    const currentIdsString = customerIds.sort().join(",");
    const lastIdsString = lastFetchedCustomerIds.current;
    const lastSearch = lastSearchQueryRef.current || "";

    const shouldFetch =
      currentIdsString !== lastIdsString ||
      debouncedSelectedSearchQuery !== lastSearch;

    if (shouldFetch) {
      lastFetchedCustomerIds.current = currentIdsString;
      lastSearchQueryRef.current = debouncedSelectedSearchQuery;
      setSelectedCustomersOffset(0);
      fetchSelectedCustomersData(
        customerIds,
        0,
        false,
        debouncedSelectedSearchQuery,
      );
    }
  }, [
    selectedCustomerIds,
    debouncedSelectedSearchQuery,
    fetchSelectedCustomersData,
  ]);

  // Re-fetch selected customers when sort changes (without ID/search change)
  const prevSelectedSortRef = useRef({ field: "", dir: "" });
  useEffect(() => {
    const prev = prevSelectedSortRef.current;
    if (prev.field !== selectedSortField || prev.dir !== selectedSortDir) {
      prevSelectedSortRef.current = {
        field: selectedSortField,
        dir: selectedSortDir,
      };
      const customerIds = Array.from(selectedCustomerIds).filter(
        (id) => id !== null && id !== undefined,
      );
      if (customerIds.length > 0) {
        setSelectedCustomersOffset(0);
        fetchSelectedCustomersData(
          customerIds,
          0,
          false,
          debouncedSelectedSearchQuery,
        );
      }
    }
  }, [
    selectedSortField,
    selectedSortDir,
    selectedCustomerIds,
    debouncedSelectedSearchQuery,
    fetchSelectedCustomersData,
  ]);

  // Sort handlers
  const handleSortClick = useCallback(() => {
    setSortDirection((prev) => nextSortDirection(prev));
  }, []);

  const handleSelectedSortClick = useCallback(() => {
    setSelectedSortDirection((prev) => nextSortDirection(prev));
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (customersLoading || !hasMore) return;

    // Abort previous request
    if (availableAbortRef.current) {
      availableAbortRef.current.abort();
    }
    const controller = new AbortController();
    availableAbortRef.current = controller;

    const newOffset = currentOffset + limit;
    setCurrentOffset(newOffset);
    setCustomersLoading(true);

    try {
      const data = await rfqNotificationsService.getCustomers(
        debouncedSearchQuery,
        limit,
        newOffset,
        availableSortField,
        availableSortDir,
        controller.signal,
        sourceIds,
      );
      const newResults = data.results || [];
      setAllCustomers((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const uniqueNew = newResults.filter((c) => !existingIds.has(c.id));
        return [...prev, ...uniqueNew];
      });
      setHasMore(newResults.length === limit);
      setCustomersLoading(false);
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        // Aborted by a newer request — do NOT clear loading, the new
        // request has already set it to true.
        return;
      }
      console.error("Failed to load more customers:", error);
      setCustomersLoading(false);
    }
  }, [
    customersLoading,
    hasMore,
    currentOffset,
    debouncedSearchQuery,
    limit,
    availableSortField,
    availableSortDir,
  ]);

  const handleLoadMoreSelected = useCallback(() => {
    if (selectedCustomersLoading || !selectedCustomersHasMore) return;
    const newOffset = selectedCustomersOffset + limit;
    setSelectedCustomersOffset(newOffset);
    const customerIds = Array.from(selectedCustomerIds).filter(
      (id) => id !== null && id !== undefined,
    );
    fetchSelectedCustomersData(
      customerIds,
      newOffset,
      true,
      debouncedSelectedSearchQuery,
    );
  }, [
    selectedCustomersLoading,
    selectedCustomersHasMore,
    selectedCustomersOffset,
    selectedCustomerIds,
    fetchSelectedCustomersData,
    limit,
    debouncedSelectedSearchQuery,
  ]);

  const handleCustomerToggle = useCallback((customerId) => {
    if (customerId === null || customerId === undefined) return;
    setSelectedCustomerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAllVisible = useCallback(() => {
    const visibleIds = allCustomers
      .map((c) => c.id)
      .filter((id) => id !== null && id !== undefined);
    const allSelected = visibleIds.every((id) => selectedCustomerIds.has(id));

    if (allSelected) {
      setSelectedCustomerIds((prev) => {
        const newSet = new Set(prev);
        visibleIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    } else {
      setSelectedCustomerIds((prev) => {
        const newSet = new Set(prev);
        visibleIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    }
  }, [allCustomers, selectedCustomerIds]);

  const handleRemoveCustomer = useCallback((customerId) => {
    setSelectedCustomerIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(customerId);
      return newSet;
    });
  }, []);

  // Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      if (availableAbortRef.current) {
        availableAbortRef.current.abort();
      }
      if (selectedAbortRef.current) {
        selectedAbortRef.current.abort();
      }
    };
  }, []);

  const availableCustomersCount = allCustomers
    .filter((c) => c.id !== null && c.id !== undefined)
    .filter((c) => !selectedCustomerIds.has(c.id)).length;

  return {
    searchQuery,
    setSearchQuery,
    selectedSearchQuery,
    setSelectedSearchQuery,
    selectedCustomerIds,
    allCustomers,
    selectedCustomersData,
    selectedCustomersLoading,
    selectedCustomersHasMore,
    availableCustomersCount,
    customersLoading,
    customersError,
    hasMore,
    handleLoadMore,
    handleLoadMoreSelected,
    handleCustomerToggle,
    handleSelectAllVisible,
    handleRemoveCustomer,
    limit,

    // Sort state & handlers - available panel
    sortDirection,
    handleSortClick,

    // Sort state & handlers - selected panel
    selectedSortDirection,
    handleSelectedSortClick,
  };
};

export default useCustomerSelection;
